// packages/api-client/src/services/admin/adminPaymentsService.ts

import { getApiClient } from '../../client';
import { handleApiError } from '../../utils/handleApiError';
import type { ServiceResponse } from '@dopomogai/types';

// === TYPE DEFINITIONS (Matching Backend) ===

export interface BasketItem {
  name: string;
  quantity: number;
  amount: number; // Per-unit price.
  unit?: string;
}

export interface AdminCreateInvoicePayload {
  items: BasketItem[];
  destination: string;
  currency: string;
  comment?: string;
  validity_days: number;
}

export interface AdminCreateInvoiceResponse {
  invoice_id: string;
  page_url: string;
}

// NEW: Type for a single invoice log entry from our DB
export interface InvoiceLog {
  id: string; // uuid
  provider: 'monobank' | 'stripe';
  provider_invoice_id: string;
  page_url: string | null;
  status: 'created' | 'processing' | 'success' | 'failure' | 'expired' | 'invalidated' | 'refunded';
  amount: number | null;
  currency: string | null;
  user_id: string | null; // uuid
  created_by_user_id: string | null; // uuid
  created_at: string; // ISO date string
  updated_at: string; // ISO date string
  metadata: Record<string, any> | null;
}

// NEW: Type for the status response from Monobank's API
export interface MonobankInvoiceStatus {
  invoiceId: string;
  status: string;
  failureReason?: string;
  amount: number;
  ccy: number;
  finalAmount?: number;
  createdDate: string;
  modifiedDate: string;
  reference?: string;
  destination?: string;
  // Add other fields from the Mono API response as needed
}

// === API SERVICE OBJECT ===

export const adminPaymentsService = {
  /**
   * Creates a new custom Monobank invoice with a detailed list of items.
   */
  createCustomInvoice: (payload: AdminCreateInvoicePayload): ServiceResponse<AdminCreateInvoiceResponse> => {
    const url = '/admin/payments/create-invoice';
    return getApiClient().post<AdminCreateInvoiceResponse>(url, payload)
      .then(res => res.data)
      .catch(err => handleApiError(err, 'creating custom Monobank invoice'));
  },

  /**
   * NEW: Lists all invoices logged in the local database.
   */
  listInvoices: (): ServiceResponse<InvoiceLog[]> => {
    const url = '/admin/payments/invoices';
    return getApiClient().get<InvoiceLog[]>(url)
      .then(res => res.data)
      .catch(err => handleApiError(err, 'listing invoices'));
  },

  /**
   * NEW: Fetches the latest status for a specific invoice directly from Monobank.
   * This also triggers a DB update on the backend.
   */
  getInvoiceStatus: (providerInvoiceId: string): ServiceResponse<MonobankInvoiceStatus> => {
    const url = `/admin/payments/invoices/${providerInvoiceId}/status`;
    return getApiClient().get<MonobankInvoiceStatus>(url)
      .then(res => res.data)
      .catch(err => handleApiError(err, `getting status for invoice ${providerInvoiceId}`));
  },

  /**
   * NEW: Invalidates an unpaid Monobank invoice.
   */
  invalidateInvoice: (providerInvoiceId: string): ServiceResponse<{ status: string; message: string }> => {
    const url = `/admin/payments/invoices/${providerInvoiceId}/invalidate`;
    return getApiClient().post<{ status: string; message: string }>(url)
      .then(res => res.data)
      .catch(err => handleApiError(err, `invalidating invoice ${providerInvoiceId}`));
  },
};